import { el, clear } from '../../dom';
import { fieldApi, type Health } from '../../game-api';
import { gicon } from '../../ui/icons';
import { authStore } from '../../../core/state/auth-store';

/**
 * HP bar — the farm HUD's top-left "vitals" readout, stacked directly ABOVE the
 * Energy panel (see design/gdd/hud-bars.md).
 *
 * Health is server-authoritative: `GET /field/health` is the source of truth and
 * every damage POST returns the fresh `Health` payload, so the bar is driven by
 * two paths:
 *   • push  — `set(h)` from the response of `/field/damage` (mob hit, poison tick)
 *   • pull  — a 2.5s poll, which is what surfaces the server's out-of-combat regen
 *
 * Below LOW_HP_FRACTION the bar pulses and a screen-fixed crimson vignette fades
 * in over the canvas (mirrors the backend's `low` flag).
 */
export const LOW_HP_FRACTION = 0.3;      // mirrors backend/src/game/field/field.config.ts
// Polling is conditional: full HP and out of combat is the common case (most of
// a session), and there is nothing for the poll to catch there but the server's
// own out-of-combat regen ticking a couple of points — a slow poll is plenty.
// The moment either stops being true (hurt, or a mob is on us) we're back to the
// tight interval, since that is exactly when the bar needs to be current.
const POLL_MS_ACTIVE = 2500;
const POLL_MS_IDLE = 10_000;

/**
 * Whether the "buy a heal" CTA belongs on screen.
 *
 * Only while wounded — a heal offer next to a full bar is a shop ad, not help.
 * Never while down: the server refuses to heal a corpse (you respawn instead), so
 * showing the button there would be a button that can only fail.
 */
export function shouldOfferHeal(h: Health | null): boolean {
  if (!h || h.dead || h.hp <= 0) return false;
  if (h.hp >= h.maxHp) return false;
  return h.low || h.hp / h.maxHp < LOW_HP_FRACTION;
}

/**
 * The numbers a purchase confirmation must state before any currency moves:
 * what it costs, what you hold, and what you will hold after. Pure so the
 * "balance after" arithmetic and the affordability verdict can be pinned in a
 * test — the popup is the last thing between a player and a spend.
 */
export function purchaseQuote(cost: number, balance: number): { affordable: boolean; after: number; short: number } {
  return { affordable: balance >= cost, after: Math.max(0, balance - cost), short: Math.max(0, cost - balance) };
}

export interface HpBarHandle {
  /** The card to mount in the HUD's stat column. */
  el: HTMLElement;
  /** Apply a Health payload we already have (e.g. a /field/damage response). */
  set(h: Health): void;
  /** Pull fresh health from the server. */
  refresh(): Promise<void>;
  destroy(): void;
}

/**
 * @param host the farm view's positioned wrapper — the low-HP vignette is
 *             appended here so it covers the canvas but not the rest of the app.
 */
export function createHpBar(host: HTMLElement): HpBarHandle {
  // No card chrome. The panel used to be a padded, bordered, blurred box in the
  // top-left; two of them stacked ate a quarter of a landscape phone's screen for
  // two numbers. It is now a bare label + bar drawn straight onto the world, with
  // `tb-hud-legible` giving the text/bar an outline so it survives on any terrain.
  const card = el('div', 'game-font tb-hud-legible w-full');
  const veil = el('div', 'absolute inset-0 pointer-events-none z-10 transition-opacity duration-300', {
    style: 'opacity:0;box-shadow:inset 0 0 120px 24px rgba(139,0,0,.75)',
  });
  host.appendChild(veil);

  let last: Health | null = null;
  let alive = true;

  function paint() {
    const h = last;
    clear(card);
    const max = h?.maxHp || 100;
    const hp = h ? Math.max(0, h.hp) : 0;
    const frac = max > 0 ? hp / max : 0;
    const low = h ? (h.low || frac < LOW_HP_FRACTION) : false;
    const dead = !!h?.dead;

    // One line: heart · bar · numbers. The old three-row card carried a status
    // caption ("Regenerating" / "In combat") on every frame; that is now a title,
    // and only the states that actually need words (down, in combat) get a chip.
    const tone = low ? 'text-error' : 'text-white/90';
    card.append(
      el('div', 'flex items-center gap-1.5 text-[11px]', {},
        gicon('heart', 13),
        el('div', 'flex-1 h-2 rounded-full bg-black/55 overflow-hidden border border-black/60', {},
          el('div', `h-full transition-all ${low ? 'bg-error animate-pulse' : 'bg-error/85'}`,
            { style: `width:${Math.round(frac * 100)}%` })),
        el('span', `font-mono ${tone}`, {}, h ? `${hp}/${max}` : '…/…')));
    card.title = dead ? 'Down — respawning at your tent'
      : h?.inCombat ? 'In combat — no regen'
        : low ? 'Wounded — back off and regen' : 'Health — regenerating';
    if (dead || h?.inCombat) {
      card.append(el('div', 'text-[10px] text-error leading-tight', {},
        dead ? 'Down — respawning' : 'In combat'));
    }
    // Wounded → an actual way out, in the place the player is already looking.
    // The modal lives in hud.ts, which pulls half the HUD with it; imported lazily
    // so the bar (and its tests) do not carry that graph for a button most frames
    // never draw.
    if (shouldOfferHeal(h)) {
      card.append(el('div', 'mt-0.5', {},
        el('button', 'tb-rest-btn text-[11px] px-2 py-0.5 rounded border border-error/70 bg-black/40 text-white/85 hover:border-error hover:text-error',
          { onClick: () => void import('./hud').then((m) => m.openHealPurchase(refresh)) }, 'Buy heal')));
    }

    veil.style.opacity = dead ? '1' : low ? String(Math.min(0.9, (LOW_HP_FRACTION - frac) / LOW_HP_FRACTION + 0.25)) : '0';
  }
  paint();

  const set = (h: Health) => { last = h; paint(); };

  let timer: number | null = null;
  /** Next poll's delay: tight while there is something to catch, slow otherwise. */
  const nextDelay = () => (!last || last.inCombat || last.hp < last.maxHp) ? POLL_MS_ACTIVE : POLL_MS_IDLE;
  const schedule = () => {
    if (!alive) return;
    // `refresh` is also called directly (e.g. the heal purchase's onDone), so any
    // timer already pending from the last poll has to go first — otherwise that
    // out-of-band call leaves two loops ticking instead of one.
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => void refresh(), nextDelay());
  };
  const refresh = async () => {
    // Stand down once there is no signed-in user. Sign-out clears the store first
    // and only then revokes the token, but this loop is on its own timer and kept
    // polling straight through it — every tick 401ing into the console while the
    // "signed out" toast was still on screen. A poller that outlives the session
    // is noise at best and a retry storm at worst.
    if (!authStore.get().user) return;
    try { const h = await fieldApi.health(); if (alive) set(h); } catch { /* HUD is best-effort */ }
    schedule();
  };
  void refresh();

  return {
    el: card,
    set,
    refresh,
    destroy() { alive = false; if (timer !== null) window.clearTimeout(timer); veil.remove(); },
  };
}
